import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {trackSaveInterfaceEvent} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../helpers/reselectHelpers';
import {CalendarEventVersionVisualStatusesEnum} from '../../../models/api/calendarEventVersionModel';
import {AppState} from '../../../store/appState';
import {
  CalendarEventSaveData,
  saveCalendarEventVersionAction,
} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {getCompany} from '../../../store/data/company/companySelectors';
import {makeGetVisualParticipants} from '../../../store/data/contacts/participantsSelectors';
import {
  getCalendars,
  getContacts,
  makeGetMasterCalendarEventVersion,
} from '../../../store/data/dataSelectors';
import {
  getIsInstantReschedulingLinkEnabled,
  getTimeFormat,
} from '../../../store/data/preferences/preferencesSelectors';
import {fetchOrCreateInstantReschedulingLinkAsyncAction} from '../../../store/data/schedulingLinks/schedulingLinksActions';
import {getInstantReschedulingLink} from '../../../store/data/schedulingLinks/schedulingLinksSelectors';
import {getUser, getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {
  getHasDefaultVideoConferencing,
  getHasInstantReschedulingLink,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {updateViewingNowCalendarsAction} from '../../../store/panels/calendars/calendarsPanelActions';
import {
  CalendarEventData,
  makeGetViewingNowCalendars,
} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventOverrides,
  ViewingNowCalendar,
} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {isProxyOrganizer} from '../calendarEventHelpers';
import {CalendarEventEditor} from './calendarEventEditor';
import {CalendarEventEditorWithNotifyGuests} from './calendarEventEditorWithNotifyGuests';

/*
 * Props.
 */

interface CalendarEventEditorContainerProps {
  panelId: CalendarsPanelId;
  calendarEventData: CalendarEventData;
  onSaveClick: Handler;
  onFocusedEventOverridesChange?: HandlerOf<CalendarEventOverrides>;
}

export interface OnCalendarEventEditorDoneArgs {
  viewingNowCalendarsToResetTo: ReadonlyArray<ViewingNowCalendar>;
  finalHandler: Handler | undefined;
}

/*
 * Selectors.
 */

function getFocusedCalendarEventData(_state: AppState, props: CalendarEventEditorContainerProps) {
  return props.calendarEventData;
}

function getCalendarEvent(_state: AppState, props: CalendarEventEditorContainerProps) {
  return props.calendarEventData.calendarEvent;
}

function makeGetIsEventOrganizer() {
  return createSelector([getFocusedCalendarEventData, getContacts], (focusedCalendarEventData, contacts) => {
    const {calendarEvent, calendarEventVersion, calendar, channel} = focusedCalendarEventData;

    // If we're creating a new event, we must be the organizer.
    if (calendarEventVersion.visualStatus === CalendarEventVersionVisualStatusesEnum.DRAFT) {
      return true;
    }

    const organizerParticipant = calendarEvent.participants.find((p) => p.isOrganizer);
    if (!organizerParticipant) {
      return false;
    }

    if (isProxyOrganizer(organizerParticipant.contactUid)) {
      return true;
    }

    const organizerContact = contacts[organizerParticipant.contactUid];
    if (!organizerContact) {
      return false;
    }

    if (calendar.organizerAddress) {
      return calendar.organizerAddress === organizerContact.handle;
    }

    return Boolean(channel && channel.sendAddress === organizerContact.handle);
  });
}

const getMasterCalendarEventVersion = makeGetMasterCalendarEventVersion(getFocusedCalendarEventData);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CalendarEventEditorContainerProps>()(() => {
  const getVisualParticipants = makeGetVisualParticipants(getUserNamespace, getCalendarEvent);
  const getIsEventOrganizer = makeGetIsEventOrganizer();
  const getViewingNowCalendars = makeGetViewingNowCalendars();

  return {
    calendars: getCalendars,
    visualParticipants: getVisualParticipants,
    isEventOrganizer: getIsEventOrganizer,
    instantReschedulingLink: getInstantReschedulingLink,
    timeFormat: getTimeFormat,
    masterCalendarEventVersion: getMasterCalendarEventVersion,
    hasDefaultVideoConferencing: getHasDefaultVideoConferencing,
    hasInstantReschedulingLinkFF: getHasInstantReschedulingLink,
    isInstantReschedulingEnabledPreference: getIsInstantReschedulingLinkEnabled,
    viewingNowCalendars: getViewingNowCalendars,
    user: getUser,
    company: getCompany,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<CalendarEventEditorContainerProps>()(() => ({
  saveCalendarEventVersionAction,
  updateViewingNowCalendarsAction,
  fetchOrCreateInstantReschedulingLinkAsync: fetchOrCreateInstantReschedulingLinkAsyncAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: CalendarEventEditorContainerProps,
) {
  const onCalendarEventEditorDone = (data: OnCalendarEventEditorDoneArgs) => {
    // Clear any added viewing now calendars.
    dispatchProps.updateViewingNowCalendarsAction(ownProps.panelId, data.viewingNowCalendarsToResetTo);
    if (data.finalHandler) {
      data.finalHandler();
    }
  };

  const onSaveClick = (data: CalendarEventSaveData) => {
    // Save the event.
    dispatchProps.saveCalendarEventVersionAction(data);

    onCalendarEventEditorDone({viewingNowCalendarsToResetTo: [], finalHandler: ownProps.onSaveClick});

    trackSaveInterfaceEvent(data, stateProps.calendars);
  };

  return {
    ...stateProps,
    ...ownProps,
    onSaveClick,
    onCalendarEventEditorDone,
    fetchOrCreateInstantReschedulingLinkAsync: dispatchProps.fetchOrCreateInstantReschedulingLinkAsync,
  };
}

export const CalendarEventEditorContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(CalendarEventEditor);

export const CalendarEventEditorWithNotifyGuestsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(CalendarEventEditorWithNotifyGuests);
